import { useEffect, useState } from "react";
import {
  getCommissionTiers,
  createCommissionTier,
  updateCommissionTier,
  deleteCommissionTier,
} from "../services/commissionTiersApi";

const emptyTier = {
  name: "",
  storeCredit: { mgr: "", asst: "" },
  commission: { mgr: "", asst: "", emp: "" },
  createdAt: "",
  updatedAt: "",
};

export default function CommissionTiers() {
  const [tiers, setTiers] = useState([]);
  const [form, setForm] = useState(emptyTier);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    loadTiers();
  }, []);

  async function loadTiers() {
    setLoading(true);
    try {
      const data = await getCommissionTiers();
      setTiers(data);
    } finally {
      setLoading(false);
    }
  }

  function handleChange(path, value) {
    setForm((prev) => {
      const next = structuredClone(prev);
      let ref = next;
      const keys = path.split(".");
      while (keys.length > 1) ref = ref[keys.shift()];
      ref[keys[0]] = value;
      return next;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const payload = {
      name: form.name,
      storeCredit: {
        mgr: Number(form.storeCredit.mgr) || 0,
        asst: Number(form.storeCredit.asst) || 0,
      },
      commission: {
        mgr: Number(form.commission.mgr) || 0,
        asst: Number(form.commission.asst) || 0,
        emp: Number(form.commission.emp) || 0,
      },
      // We don't send createdAt or updatedAt here, backend should manage timestamps
    };

    if (editing) {
      await updateCommissionTier(editing, payload);
    } else {
      await createCommissionTier(payload);
    }

    setForm(emptyTier);
    setEditing(null);
    setModalOpen(false);
    loadTiers();
  }

  function handleEdit(tier) {
    setForm({
      name: tier.name,
      storeCredit: {
        mgr: tier.storeCredit.mgr ?? 0,
        asst: tier.storeCredit.asst ?? 0,
      },
      commission: {
        mgr: tier.commission.mgr ?? 0,
        asst: tier.commission.asst ?? 0,
        emp: tier.commission.emp ?? 0,
      },
      createdAt: tier.createdAt ?? "",
      updatedAt: tier.updatedAt ?? "",
    });
    setEditing(tier.name);
    setModalOpen(true);
  }

  function handleCancel() {
    setForm(emptyTier);
    setEditing(null);
    setModalOpen(false);
  }

  async function handleDelete(name) {
    if (!window.confirm(`Delete tier "${name}"?`)) return;
    await deleteCommissionTier(name);
    loadTiers();
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              Commission Tiers
            </h1>
            <p className="text-slate-600">
              Manage commission rates and store credit for different employee
              roles
            </p>
          </div>
          <button
            onClick={() => {
              setForm(emptyTier);
              setEditing(null);
              setModalOpen(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
          >
            + Add New Tier
          </button>
        </div>

        {/* Tiers List */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden mb-10">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block w-8 h-8 border-4 border-slate-300 border-t-blue-600 rounded-full animate-spin"></div>
              <p className="mt-3 text-slate-600">Loading tiers...</p>
            </div>
          ) : tiers.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <p className="text-slate-600 font-medium">No tiers found</p>
              <p className="text-sm text-slate-500 mt-1">
                Add your first commission tier to get started
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Tier Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Store Sale
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Individual Commission
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Updated At
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {tiers.map((t) => (
                    <tr
                      key={t.name}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <span className="font-semibold text-slate-900">
                          {t.name}
                        </span>
                      </td>
                      {/* <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 w-16 justify-center">
                            M: {t.storeCredit.mgr ?? 0}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 w-16 justify-center">
                            A: {t.storeCredit.asst ?? 0}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200 w-16 justify-center">
                            E: 0
                          </span>
                        </div>
                      </td> */}

                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 w-16 justify-center">
                            M: {Number(t.storeCredit.mgr ?? 0).toFixed(2)}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 w-16 justify-center">
                            A: {Number(t.storeCredit.asst ?? 0).toFixed(2)}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200 w-16 justify-center">
                            E: {Number(0).toFixed(2)}
                          </span>
                        </div>
                      </td>
                      {/* <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 w-16 justify-center">
                            M: {t.commission.mgr ?? 0}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 w-16 justify-center">
                            A: {t.commission.asst ?? 0}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 w-16 justify-center">
                            E: {t.commission.emp ?? 0}
                          </span>
                        </div>
                      </td> */}

                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 w-16 justify-center">
                            M: {Number(t.commission.mgr ?? 0).toFixed(2)}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 w-16 justify-center">
                            A: {Number(t.commission.asst ?? 0).toFixed(2)}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 w-16 justify-center">
                            E: {Number(t.commission.emp ?? 0).toFixed(2)}
                          </span>
                        </div>
                      </td>

                      {/* CreatedAt */}
                      <td className="px-6 py-4 text-xs text-slate-600">
                        {t.createdAt
                          ? new Date(t.createdAt).toLocaleString()
                          : "-"}
                      </td>

                      {/* UpdatedAt */}
                      <td className="px-6 py-4 text-xs text-slate-600">
                        {t.updatedAt
                          ? new Date(t.updatedAt).toLocaleString()
                          : "-"}
                      </td>

                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleEdit(t)}
                          className="text-blue-600 hover:text-blue-800 font-medium text-sm mr-4 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(t.name)}
                          className="text-red-600 hover:text-red-800 font-medium text-sm transition-colors"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal */}
        {modalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            onClick={handleCancel}
          >
            <div
              className="bg-white rounded-xl shadow-lg p-6 w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-slate-800">
                  {editing ? `Edit Tier: ${editing}` : "Add New Tier"}
                </h2>
                <button
                  onClick={handleCancel}
                  className="text-slate-600 hover:text-slate-900 font-medium"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Tier Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Tier Name
                  </label>
                  <input
                    required
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                    placeholder="e.g., 110%, FLAT, -10%"
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    disabled={!!editing} // disable editing name when updating
                  />
                </div>

                {/* Store Credit Section */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                    Store Sales
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">
                        Manager
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition"
                        value={form.storeCredit.mgr}
                        onChange={(e) =>
                          handleChange("storeCredit.mgr", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">
                        Assistant
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition"
                        value={form.storeCredit.asst}
                        onChange={(e) =>
                          handleChange("storeCredit.asst", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Commission Section */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    Individual Sales
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">
                        Manager
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                        value={form.commission.mgr}
                        onChange={(e) =>
                          handleChange("commission.mgr", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">
                        Assistant
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                        value={form.commission.asst}
                        onChange={(e) =>
                          handleChange("commission.asst", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">
                        Employee
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                        value={form.commission.emp}
                        onChange={(e) =>
                          handleChange("commission.emp", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* CreatedAt and UpdatedAt info - readonly */}
                {editing && (
                  <div className="text-xs text-slate-500 space-y-1">
                    <p>
                      <strong>Created At:</strong>{" "}
                      {form.createdAt
                        ? new Date(form.createdAt).toLocaleString()
                        : "N/A"}
                    </p>
                    <p>
                      <strong>Updated At:</strong>{" "}
                      {form.updatedAt
                        ? new Date(form.updatedAt).toLocaleString()
                        : "N/A"}
                    </p>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 font-medium shadow-md hover:shadow-lg transition-all duration-200"
                >
                  {editing ? "Update Tier" : "Add Tier"}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
